// Session state and server events (E01-S4-T3; contracts 1.0 section 7). Every server event carries
// its connection's `seq` (ADR-028); a gap makes the client send resync (D-55). `state` is authoritative, complete
// and idempotent: a client replaces its state wholesale and never merges, so SessionState has no
// optional top-level key. A refusal says why on the say event (D-07).
import { z } from "zod";
import { Citation } from "../citation.js";
import { PersonaId, WorkingMode } from "../pack.js";
import { Count, Id, IsoUtc, NonEmpty, PerceptionTier, strictObject } from "../primitives.js";
import { RecordEntry } from "../record.js";
import { RefusalReason } from "../refusal.js";
import { ScopedLoopId, WalkthroughState } from "../sop.js";
import { TripwireHighlight } from "../tripwire.js";
import { Card } from "./cards.js";
import { NavigateRequest } from "./client.js";
import { Nudge, Suggestion } from "./nudge.js";
export const SessionState = strictObject({
    session: strictObject({
        id: NonEmpty,
        persona: PersonaId,
        mode: WorkingMode,
        scaffolding: z.enum(["full", "light", "quiet"]),
        practice: z.boolean(),
        voiceViewId: NonEmpty.optional(), // the one view that receives speech (D-87)
    }),
    seeing: strictObject({
        sourceId: NonEmpty.nullable(),
        screenId: z.union([Id, z.literal("unknown")]),
        title: NonEmpty,
        known: z.boolean(),
        tier: PerceptionTier,
        paused: z.boolean(),
    }),
    walkthrough: WalkthroughState.nullable(),
    nudges: z.array(Nudge),
    openLoops: z.array(strictObject({ id: ScopedLoopId, text: NonEmpty, done: z.boolean() })),
});
export const MessageRecord = strictObject({
    messageId: NonEmpty,
    role: z.enum(["user", "assistant"]),
    sentences: z.array(NonEmpty),
    cites: z.array(Citation),
    at: IsoUtc,
    // ADR-026: false only while sentences are still arriving for this messageId (the last say.final).
    final: z.boolean(),
});
export const Highlight = strictObject({
    targets: z.array(TripwireHighlight).min(1),
    dim: z.boolean().optional(),
});
/** D-35: one sentence of synthesised speech is at most 512 KiB of base64. */
export const SPEECH_B64_MAX = 512 * 1024;
/** Standard padded base64, checked by a loop: a regex over half a megabyte risks its backtrack stack. */
export function isBase64(s) {
    if (s.length % 4 !== 0)
        return false;
    const pad = s.endsWith("==") ? 2 : s.endsWith("=") ? 1 : 0;
    for (let i = 0; i < s.length - pad; i++) {
        const c = s.charCodeAt(i);
        const ok = (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || (c >= 48 && c <= 57) || c === 43 || c === 47;
        if (!ok)
            return false;
    }
    return true;
}
export const SpeechAudio = strictObject({
    b64: z.string().min(1).max(SPEECH_B64_MAX).refine(isBase64, "expected standard padded base64"),
    mime: z
        .string()
        .regex(/^audio\/[a-z0-9.+-]+(?:;\s*[a-z0-9-]+=[A-Za-z0-9.-]+)*$/, "expected an audio media type"),
});
const seq = Count;
/** A card as it stands in a snapshot: the same pair a `card` event carries (ADR-026). */
const SnapshotCard = strictObject({ id: NonEmpty, card: Card });
export const ServerEvent = z.discriminatedUnion("t", [
    strictObject({ seq, t: z.literal("welcome"), sessionId: NonEmpty, resumeToken: NonEmpty }),
    // EdgePolicy as a compact JWS, signed with the edge key (D-41).
    strictObject({
        seq,
        t: z.literal("policy"),
        jws: z
            .string()
            .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, "expected a compact JWS"),
    }),
    // ADR-026: rev is the revision of the state it carries; cards are the same {id, card} pairs a
    // card event carries, so a late view can match a later card event to one it already holds.
    strictObject({
        seq,
        t: z.literal("snapshot"),
        rev: Count,
        state: SessionState,
        messages: z.array(MessageRecord),
        record: z.array(RecordEntry),
        highlight: Highlight.nullable(),
        cards: z.array(SnapshotCard),
    }),
    strictObject({ seq, t: z.literal("state"), rev: Count, state: SessionState }),
    strictObject({
        seq,
        t: z.literal("say"),
        messageId: NonEmpty,
        index: Count,
        sentence: NonEmpty,
        cites: z.array(Citation),
        suggestions: z.array(Suggestion).optional(),
        refusal: strictObject({ reason: RefusalReason }).optional(),
        final: z.boolean(),
    }),
    strictObject({
        seq,
        t: z.literal("speech"),
        messageId: NonEmpty,
        index: Count,
        audio: SpeechAudio.nullable(),
    }),
    strictObject({
        seq,
        t: z.literal("transcript"),
        utteranceId: NonEmpty,
        text: NonEmpty,
        final: z.boolean(),
    }),
    strictObject({ seq, t: z.literal("nudge"), nudge: Nudge }),
    strictObject({ seq, t: z.literal("highlight"), highlight: Highlight.nullable() }),
    strictObject({ seq, t: z.literal("card"), id: NonEmpty, card: Card }),
    strictObject({
        seq,
        t: z.literal("offer"),
        id: NonEmpty,
        kind: z.literal("navigate"),
        request: NavigateRequest,
        text: NonEmpty,
    }),
    strictObject({ seq, t: z.literal("record"), entries: z.array(RecordEntry) }),
    // code is a plain string as the contract has it; the known codes are in errors.ts.
    strictObject({
        seq,
        t: z.literal("error"),
        code: NonEmpty,
        retryable: z.boolean(),
        ref: strictObject({ messageId: NonEmpty.optional(), cid: NonEmpty.optional() }).optional(),
    }),
    strictObject({ seq, t: z.literal("pong") }),
]);
/**
 * The state event to keep: the higher rev. Equal revs are the same state, so `a` is kept — the
 * answer does not depend on arrival order and applying the same state twice changes nothing. A
 * snapshot carries its state's rev (ADR-026), so the same rule decides whether a later state
 * replaces the state a snapshot gave.
 */
export function newerState(a, b) {
    return b.rev > a.rev ? b : a;
}
//# sourceMappingURL=server.js.map